import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Popover, Icon } from 'antd'
import {
  AlphaPicker,
  BlockPicker,
  ChromePicker,
  CirclePicker,
  CompactPicker,
  GithubPicker,
  HuePicker,
  MaterialPicker,
  PhotoshopPicker,
  SketchPicker,
  SliderPicker,
  SwatchesPicker,
  TwitterPicker,
} from 'react-color'
import cn from 'classnames'
import { get, isObject, upperFirst, toLower } from 'lodash'
import imgSrc from './tip.svg'

const prefixCls = 'tntd-color-picker'

const modeMap = {
  AlphaPicker,
  BlockPicker,
  ChromePicker,
  CirclePicker,
  CompactPicker,
  GithubPicker,
  HuePicker,
  MaterialPicker,
  PhotoshopPicker,
  SketchPicker,
  SliderPicker,
  SwatchesPicker,
  TwitterPicker,
}

const propsFormatMap = {
  SketchPicker: ({ colors, ...otherProps }) => {
    if (colors) {
      return {
        presetColors: colors,
        ...otherProps,
      }
    }
    return otherProps
  },
}

export default function ColorPicker({
  value: _value,
  onChange,
  visible: vs,
  allowClear,
  disabled,
  className,
  mode = 'sketch',
  style = {},
  size,
  placeholder,
  placement = 'bottomLeft',
  locale,
  ...otherProps
}) {
  const [visible, setVisible] = useState(vs)
  const [value, setValue] = useState(_value)

  useEffect(() => {
    setValue(_value)
  }, [_value])

  const handleChange = useCallback(
    (color) => {
      let value
      if (isObject(color?.rgb)) {
        value = `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`
      }
      if (onChange) {
        onChange(value)
      } else {
        setValue(value)
      }
    },
    [onChange]
  )

  const domClick = useCallback(() => {
    setVisible(true)
  }, [])

  const doClear = useCallback(
    (e) => {
      e.stopPropagation()
      handleChange()
    },
    [handleChange]
  )

  const [ModePicker, propsFormat] = useMemo(() => {
    const modeKey = upperFirst(toLower(mode)) + 'Picker' || 'SketchPicker'
    const ModePicker = modeMap[modeKey] || SketchPicker
    return [ModePicker, propsFormatMap[modeKey] || ((p) => p)]
  }, [mode])

  return (
    <Popover
      placement={placement}
      trigger="click"
      visible={visible && !disabled}
      onVisibleChange={setVisible}
      content={
        <ModePicker {...propsFormat(otherProps)} color={value} onChangeComplete={handleChange} />
      }
      overlayClassName={`${prefixCls}-pop`}
    >
      <div
        className={cn(prefixCls, className, {
          [`${prefixCls}-selected`]: !!value,
          [`${prefixCls}-disabled`]: disabled,
          [`${prefixCls}-active`]: visible && !disabled,
          [`${prefixCls}-sm`]: size === 'small',
          [`${prefixCls}-lg`]: size === 'large',
        })}
        style={style}
        onClick={domClick}
      >
        <div className={`${prefixCls}-inner`}>
          {!!value ? (
            <div className={`${prefixCls}-value`}>
              <div className={`${prefixCls}-value-bg`} style={{ background: value }}></div>
            </div>
          ) : (
            <div className={`${prefixCls}-placeholder`}>{placeholder}</div>
          )}
          <span className={`${prefixCls}-img`}>
            <img src={imgSrc} />
          </span>
          {allowClear && !!value && !disabled && (
            <span className={`${prefixCls}-clear`} onClick={doClear}>
              <Icon type="close-circle" theme="filled" />
            </span>
          )}
        </div>
      </div>
    </Popover>
  )
}
